import { useEffect, useRef } from "react";

/**
 * Makes an in-page overlay (modal / sheet) dismissable with the browser or
 * hardware **Back** button while keeping the visitor exactly where they are.
 *
 * The problem it solves: an overlay that lives in component state pushes no
 * history entry, so on `/app?city=X` pressing Back pops that URL and drops the
 * visitor back at the city gate — instead of just closing the thing they opened.
 *
 * While `open`, a synthetic history entry (same URL) is pushed so Back has
 * something to pop; a `popstate` listener then runs `close`. Closing through the
 * UI (X / Escape / backdrop) flips `open` to false and the cleanup pops the
 * synthetic entry so history stays balanced (no dead Back presses pile up).
 *
 * Scope: apply this to a SINGLE overlay type at a time. It intentionally does
 * not coordinate nested overlays — no other code in the app listens for
 * `popstate`, so the one listener here is unambiguous.
 *
 * Dev note: under `React.StrictMode` the effect is intentionally double-invoked
 * in development, which can make the synthetic entry get pushed/popped twice and
 * the overlay close on open. This does NOT happen in the production build (React
 * does not double-invoke effects there) — which is what ships and what the e2e
 * suite drives against the built `dist`.
 */
export function useHistoryBackedOverlay(open: boolean, close: () => void, url?: string | null) {
  const closeRef = useRef(close);
  closeRef.current = close;
  const urlRef = useRef(url);
  urlRef.current = url;

  // `url` (optional) puts the overlay's own address in the bar while it is
  // open, e.g. the venue page `/cluj-pomet` over the `/cluj` feed. It is a bare
  // pushState, so React Router doesn't route and nothing re-renders; Back pops
  // it to the feed URL the router never left.
  useEffect(() => {
    if (!open || !url) return;
    const state = window.history.state as { __overlay?: boolean } | null;
    if (state?.__overlay && window.location.pathname !== url) {
      window.history.replaceState({ __overlay: true }, "", url);
    }
  }, [open, url]);

  useEffect(() => {
    if (!open) return;
    const target = urlRef.current;
    if (target) window.history.pushState({ __overlay: true }, "", target);
    else window.history.pushState({ __overlay: true }, "");
    let poppedByBack = false;

    const onPop = () => {
      // Back already removed our entry — just close; don't pop again.
      poppedByBack = true;
      closeRef.current();
    };
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
      // Closed via the UI while our entry is still on top → remove it so the
      // next Back press isn't wasted on a stale, same-URL synthetic entry.
      if (!poppedByBack && (window.history.state as { __overlay?: boolean } | null)?.__overlay) {
        window.history.back();
      }
    };
  }, [open]);
}
